import { useState } from 'react';
import OnboardingWizard from '../../dummies/OnboardingWizard/OnboardingWizard';
import OrganizationSetupForm from '../../dummies/OrganizationSetupForm/OrganizationSetupForm';
import DashboardTemplateSelector from '../../dummies/DashboardTemplateSelector/DashboardTemplateSelector';
import { uploadImage, deleteImage, completeOnboarding } from './actions/onboardingActions';

/**
 * OnboardingWizardFlow - Business Component (סטנד-אלון)
 * מנהל את הלוגיקה של תהליך ההכנסה למערכת
 * מקבל הכל דרך props - ללא גישה ישירה ל-Redux!
 * 
 * @param {boolean} open - האם הויזרד פתוח
 * @param {object} user - המשתמש המחובר
 * @param {object} organization - ארגון (לצורך הצגת הדשבורד)
 * @param {object} catalog - קטלוג הווידג'טים
 * @param {array}  - תבניות מוכנות מראש מ-Firestore
 * @param {function} dispatch - Redux dispatch
 * @param {function} navigate - פונקציית ניווט
 */
const OnboardingWizardFlow = ({
    open,
    user,
    organization,
    catalog,
    predefinedTemplates = [],
    dispatch,
    navigate,
}) => {
    // Local state
    const [activeStep, setActiveStep] = useState(0);

    // Organization form state
    const [name, setName] = useState('');
    const [country] = useState('ישראל');
    const [selectedCity, setSelectedCity] = useState(null);
    const [street, setStreet] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [logoUrl, setLogoUrl] = useState('');

    const [uploading, setUploading] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [isCompleting, setIsCompleting] = useState(false);

    // Template selection - שילוב תבניות מוכנות מראש מ-Firestore
    // התבניות המוכנות מופיעות קודם, המסך הריק בסוף
    const templates = [
        ...predefinedTemplates,
        {
            id: 'empty',
            name: 'לוח ריק',
            category: 'כללי',
            widgets: [],
            settings: { bgColor: '#fcfbfa', bgImage: '' }
        }
    ];
    const [selectedTemplateId, setSelectedTemplateId] = useState(predefinedTemplates.length > 0 ? predefinedTemplates[0].id : 'empty');

    // בדיקה אם הטופס תקין
    const isFormValid = name && country && selectedCity && street && houseNumber;

    const handleLogoUpload = async (file) => {
        if (!file.type.startsWith('image/')) {
            setValidationError('יש להעלות קובץ תמונה בלבד');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setValidationError('גודל הקובץ חייב להיות עד 5MB');
            return;
        }

        setValidationError('');
        setUploading(true);

        try {
            const imageUrl = await uploadImage(file, 'images');
            setLogoUrl(imageUrl);
        } catch (err) {
            setValidationError('שגיאה בהעלאת התמונה');
        } finally {
            setUploading(false);
        }
    };

    const handleLogoDelete = async () => {
        if (!logoUrl) return;

        try {
            await deleteImage(logoUrl);
            setLogoUrl('');
        } catch (err) {
            console.error('Error deleting logo:', err);
        }
    };

    const handleNext = () => {
        if (activeStep < 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleFinish = async () => {
        setIsCompleting(true);
        setValidationError('');

        // בניית אובייקט organizationData מהמשתנים הפרטניים
        const organizationData = {
            name,
            address: {
                country,
                city: selectedCity?.name || '',
                street,
                houseNumber,
            },
            location: selectedCity ? {
                lat: selectedCity.lat,
                lng: selectedCity.lng
            } : null,
            logoUrl,
        };

        // מציאת התבנית הנבחרת
        const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

        // יצירת ארגון עם התבנית הנבחרת
        const result = await dispatch(completeOnboarding(user, organizationData, selectedTemplate, navigate));

        if (!result.success) {
            setValidationError(result.error);
            setIsCompleting(false);
        }
        // אם הצליח, הניווט כבר קרה ב-action
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <OrganizationSetupForm
                        name={name}
                        onNameChange={setName}
                        country={country}
                        selectedCity={selectedCity}
                        onCityChange={setSelectedCity}
                        street={street}
                        onStreetChange={setStreet}
                        houseNumber={houseNumber}
                        onHouseNumberChange={setHouseNumber}
                        logoUrl={logoUrl}
                        uploading={uploading}
                        isSubmitting={isCompleting}
                        validationError={validationError}
                        onLogoUpload={handleLogoUpload}
                        onLogoDelete={handleLogoDelete}
                    />
                );
            case 1:
                // יצירת organization דמה לתצוגה מקדימה (הארגון נוצר רק בסוף הוויזרד)
                const previewOrganization = {
                    organizationId: 'preview-org',
                    name: 'תצוגה מקדימה',
                    address: {
                        city: 'ירושלים',
                        country: 'ישראל'
                    },
                    location: {
                        lat: 31.7683,
                        lng: 35.2137
                    }
                };

                return (
                    <DashboardTemplateSelector
                        templates={templates}
                        selectedTemplateId={selectedTemplateId}
                        onSelectTemplate={setSelectedTemplateId}
                        organization={previewOrganization}
                        catalog={catalog}
                        dispatch={dispatch}
                        loading={false}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <OnboardingWizard
            open={open}
            activeStep={activeStep}
            onNext={handleNext}
            onBack={handleBack}
            onFinish={handleFinish}
            isNextDisabled={activeStep === 0 && !isFormValid}
            isLoading={isCompleting}
        >
            {renderStepContent()}
        </OnboardingWizard>
    );
};

export default OnboardingWizardFlow;
